import { useEffect, useState } from 'react';
import type { ThirdPartyMessageStatus } from '../../../../../../common/enums/thirdPartyMessageStatus';
import type { IRequest } from '../../../../../../common/interfaces/request';
import type { IProcedure } from '../../../../interfaces/procedure';
import type { IRequestGetOne } from '../../../../interfaces/requestGetOne';
import type { ICustomControlRequestReadProps } from '../../interfaces/customControlRequestReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { RequestStatus } from '../../../../../../common/enums/requestStatus';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { getStatusLabel } from '../../../../elements/getStatusLabel';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { GgzbTherV1FollowingSteps } from './GgzbTherV1FollowingStepts';
import { GgzbTherV1RequestReadDraft } from './GgzbTherV1RequestReadDraft';
import { GgzbTherV1RequestReadSent } from './GgzbTherV1RequestReadSent';

export interface IProcedureInvitation extends IProcedure {
    failedToGetProgress: boolean;
    request: IRequest;
    thirdPartyMessageStatus: ThirdPartyMessageStatus;
}

interface INotUpdatedInvitations {
    unableToUpdateAllInvitations: boolean;
    unableToUpdateRequestIds: string[];
}

export function GgzbTherV1RequestRead(props: ICustomControlRequestReadProps) {
    const [invitations, setInvitations] = useState<IProcedureInvitation[] | null>(null);
    const { process, itemState, request, isFirstItem } = props;

    useEffect(() => {
        updateAndLoadInvitations(request);
    }, []);

    return (
        <ProcessContainer isActiveActivity={itemState.initialIsOpen}>
            <ProcessItemHeader
                date={process.dateSent || request?.dateSent}
                isFirstItem={isFirstItem}
                isFirstProcess={itemState.isFirstProcess}
                isItemOpen={itemState.isOpen}
                onToggle={async (isOpened) => {
                    await onToggle(isOpened || !!itemState.initialIsOpen);
                }}
                organizationName={process.requesterOrganization?.name}
                practitionerName={
                    process.requesterPractitioner ? getDisplayName(process.requesterPractitioner) : undefined
                }
                process={{ ...process, statusLabel: getStatusLabel(process) }}
                serviceType={process.serviceType}
                title={getServiceLabel(process.code, 'request', process.name)}
            />
            <CustomControlCollapseBody isOpen={itemState.isOpen}>
                {request.status === RequestStatus.draft ? (
                    <GgzbTherV1RequestReadDraft {...props} />
                ) : (
                    <GgzbTherV1RequestReadSent
                        {...props}
                        invitations={invitations}
                    />
                )}
                <GgzbTherV1FollowingSteps />
            </CustomControlCollapseBody>

            {!itemState.initialIsOpen && (
                <ProcessItemFooter
                    isItemOpen={itemState.isOpen}
                    labelLess={translateKey('request.hideCounseling')}
                    labelMore={translateKey('request.showCounseling')}
                    onToggle={onToggle}
                />
            )}
        </ProcessContainer>
    );

    async function onToggle(isOpened: boolean): Promise<void> {
        await props.updateItemState({
            ...itemState,
            isOpen: isOpened,
        });
    }

    async function updateAndLoadInvitations(inviteRequest: IRequestGetOne): Promise<void> {
        const notUpdatedInvitations = (
            await fetchJson(`/api/procedures/${inviteRequest.id}/updateInvitationsForCounseling`, 'PUT')
        ).json as INotUpdatedInvitations;
        const result = (await fetchJson(`/api/procedures/${inviteRequest.id}/getInvitationsForCounseling`, 'GET')).json
            .items as IProcedureInvitation[];
        const invitationsWithProgress = updateInvitationsWithProgress(result, notUpdatedInvitations);
        setInvitations(invitationsWithProgress);
    }

    function updateInvitationsWithProgress(
        procedureInvitations: IProcedureInvitation[],
        notUpdatedInvitations: INotUpdatedInvitations
    ) {
        if (notUpdatedInvitations.unableToUpdateAllInvitations) {
            return procedureInvitations.map((invitation) => {
                return {
                    ...invitation,
                    failedToGetProgress: true,
                };
            });
        }

        return procedureInvitations.map((invitation) => {
            const failedToGetProgress = !!notUpdatedInvitations.unableToUpdateRequestIds.find(
                (requestId) => requestId === invitation.request.id
            );

            return {
                ...invitation,
                failedToGetProgress,
            };
        });
    }
}
